import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useDeepLinkParam(paramName, onFound)
 *
 * THE capture/strip half of a deep-link query param (`?gag=`, `?order=`,
 * ...): read an integer id out of the URL, strip it from the URL
 * IMMEDIATELY via `setSearchParams(next, { replace: true })` so a refresh
 * cannot re-trigger it, and hand the parsed id to the caller's own
 * `onFound(id)`. Any other query param survives — only `paramName` is
 * deleted.
 *
 * Extracted 2026-09-01 from `GagBreakdown.js`'s `?gag=` effect and
 * `DepartmentOrders.js`'s `?order=` effect, which were byte-for-byte
 * identical apart from the param name and what `onFound` does with the
 * parsed id. This hook owns ONLY the capture/strip step — "what happens
 * once an id is captured" (retry-once-after-a-filter-reset, a toast on
 * not-found, jumping to a page, expanding a row, routing to a view...) is
 * entirely different between the two real callers and stays in their own
 * separate resolve effects, unmoved.
 *
 * 🔴 `MyTeamTasks.js`'s `?task=` effect was READ and is DELIBERATELY NOT
 * migrated to this hook — see the comment at its call site for why. It
 * does not strip the param and carries no ref-reset step at all: a single
 * effect does the lookup and the expand inline, with no separate
 * "pending id" indirection. Forcing it through this hook would mean
 * either changing its behaviour (stripping a param it currently leaves in
 * the URL) or adding an option this hook's two genuine callers never
 * need. Extracting for two near-verbatim copies is a real win; bending a
 * third, materially different shape to fit is the "no duplication unless
 * absolutely required" rule read backwards — a bad shared abstraction is
 * worse than the honest duplication it replaces.
 *
 * `onFound` runs UNCONDITIONALLY every time the param is captured — not
 * only when the parsed id differs from some previous value. This
 * preserves the exact behaviour both real callers had: their own
 * retry-guard ref is reset on every fresh capture, including a second
 * deep link to the SAME id.
 *
 * @param {string} paramName — the query param to capture (e.g. 'gag', 'order')
 * @param {(id: number) => void} onFound — called with the parsed id once
 *   per capture; the caller owns whatever "found" means for its own state
 *   (a `pendingXId` setter, a retry-guard ref reset, etc.)
 */
export default function useDeepLinkParam(paramName, onFound) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const raw = searchParams.get(paramName);
    if (!raw) return;
    const id = parseInt(raw, 10);
    const next = new URLSearchParams(searchParams);
    next.delete(paramName);
    setSearchParams(next, { replace: true });
    if (!isNaN(id)) onFound(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, paramName]);
}
